import { useMemo } from "react";
import type { AuthContextValue } from "@/contexts/AuthContext";
import { formatMemberName, type MemberWithProfile } from "./adminTypes";
import { ActivityTimelineList } from "@/components/activity/ActivityTimelineList";
import { useActivityTimeline } from "@/lib/activityTimeline/useActivityTimeline";
import {
  TIMELINE_DOMAIN_OPTIONS,
  TIMELINE_TEST_DATA_OPTIONS,
} from "@/lib/activityTimeline/types";

const ORG_TIMELINE_ROLES = ["owner", "admin", "org_owner", "org_admin"] as const;
const PLATFORM_TIMELINE_ROLES = ["platform_owner", "platform_admin", "platform_dev"] as const;

function canViewOrgActivityTimeline(auth: AuthContextValue): boolean {
  if (auth.status !== "ready") return false;
  if (auth.realHasRole(...PLATFORM_TIMELINE_ROLES)) return true;
  return auth.hasRole(...ORG_TIMELINE_ROLES);
}

type Props = {
  organizationId: string;
  enabled: boolean;
  members: MemberWithProfile[];
  auth: AuthContextValue;
  plain?: boolean;
};

export function AdminActivityTimelineSection({
  organizationId,
  enabled,
  members,
  auth,
  plain,
}: Props) {
  const canView = canViewOrgActivityTimeline(auth);
  const timeline = useActivityTimeline({
    enabled: enabled && canView,
    scope: "org",
    organizationId,
    includeSourceFilter: false,
  });

  const actorOptions = useMemo(() => {
    const rows = [...members].sort((a, b) =>
      formatMemberName(a).localeCompare(formatMemberName(b), undefined, { sensitivity: "base" })
    );
    return rows.map((m) => ({
      id: m.user_id,
      label: formatMemberName(m),
    }));
  }, [members]);

  if (!canView) {
    return (
      <p className={`text-sm text-gray-500 dark:text-zinc-400 ${plain ? "px-4 py-3" : ""}`}>
        Activity timeline is restricted to org admins and owners.
      </p>
    );
  }

  const selectClass =
    "rounded-lg border border-gray-300 px-2 py-1.5 text-sm dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100";
  const inputClass =
    "rounded-lg border border-gray-300 px-2 py-1.5 text-sm dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100";
  const pad = plain ? "px-4 pb-4 pt-3" : "";

  return (
    <div className={`space-y-3 ${pad}`}>
      <p className="text-xs text-gray-600 dark:text-zinc-400">
        Unified org activity for the last 7 days by default — leads, order intake, manufacturing
        audit rows, and more as federation expands.
      </p>

      <div className="flex flex-wrap items-end gap-2">
        <div>
          <label className="mb-1 block text-xs text-gray-500">From</label>
          <input
            className={inputClass}
            type="date"
            value={timeline.filters.sinceDate}
            onChange={(e) => timeline.patchFilters({ sinceDate: e.target.value })}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-gray-500">To</label>
          <input
            className={inputClass}
            type="date"
            value={timeline.filters.untilDate}
            onChange={(e) => timeline.patchFilters({ untilDate: e.target.value })}
          />
        </div>
        <div className="min-w-[10rem]">
          <label className="mb-1 block text-xs text-gray-500">Actor</label>
          <select
            className={`${selectClass} w-full`}
            value={timeline.filters.actorUserId}
            onChange={(e) => timeline.patchFilters({ actorUserId: e.target.value })}
          >
            <option value="">All actors</option>
            {actorOptions.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs text-gray-500">Domain</label>
          <select
            className={selectClass}
            value={timeline.filters.domain}
            onChange={(e) =>
              timeline.patchFilters({
                domain: e.target.value as typeof timeline.filters.domain,
              })
            }
          >
            {TIMELINE_DOMAIN_OPTIONS.map((o) => (
              <option key={o.label} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs text-gray-500">Entity type</label>
          <input
            className={inputClass}
            type="text"
            placeholder="lead, mfg_material…"
            value={timeline.filters.entityType}
            onChange={(e) => timeline.patchFilters({ entityType: e.target.value })}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-gray-500">Entity id</label>
          <input
            className={`${inputClass} font-mono text-xs`}
            type="text"
            placeholder="uuid drill-down"
            value={timeline.filters.entityId}
            onChange={(e) => timeline.patchFilters({ entityId: e.target.value })}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-gray-500">Action</label>
          <input
            className={inputClass}
            type="text"
            placeholder="sent, update…"
            value={timeline.filters.action}
            onChange={(e) => timeline.patchFilters({ action: e.target.value })}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-gray-500">Test data</label>
          <select
            className={selectClass}
            value={timeline.filters.testData}
            onChange={(e) =>
              timeline.patchFilters({
                testData: e.target.value as typeof timeline.filters.testData,
              })
            }
          >
            {TIMELINE_TEST_DATA_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="min-w-[10rem] flex-1">
          <label className="mb-1 block text-xs text-gray-500">Search</label>
          <input
            className={`${inputClass} w-full`}
            type="search"
            placeholder="summary, customer name…"
            value={timeline.filters.q}
            onChange={(e) => timeline.patchFilters({ q: e.target.value })}
          />
        </div>
        <button
          type="button"
          className="rounded-lg bg-gray-900 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50 dark:bg-zinc-100 dark:text-zinc-900"
          onClick={() => void timeline.refresh()}
          disabled={timeline.working}
        >
          {timeline.working ? "Loading…" : "Refresh"}
        </button>
      </div>

      {timeline.error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-100">
          {timeline.error}
        </div>
      ) : null}

      {timeline.federationTruncated ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100">
          Results may be truncated — narrow the date range or add filters.
        </div>
      ) : null}

      <ActivityTimelineList
        entries={timeline.entries}
        total={timeline.total}
        loading={timeline.loading}
        error={null}
        scope="org"
        hasMore={timeline.hasMore}
        working={timeline.working}
        onLoadMore={() => void timeline.loadMore()}
        onDrillEntity={timeline.enterEntityDrill}
        drillEntityId={timeline.filters.entityId.trim() || null}
        onClearDrill={timeline.clearEntityDrill}
        emptyMessage="No activity matches these filters."
      />
    </div>
  );
}
